import React from 'react';
import {
  Button,
  // createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText
  // makeStyles,
  // Typography
} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {
  sendReminders,
  updateGuarantorNotificationPhone
} from '@shared/services/DueInvoiceServices';
import {validateNotificationPhoneHookForm} from '@shared/modules/invoice/Invoice';
import {Controller, useForm} from 'react-hook-form';
import PhoneInputField from '@components/system/PhoneInputField/PhoneInputField';
import './ChangePhoneDialogStyles.scss';
import HelperUtils from '@shared/utils/HelperUtils';
// interface for Confirmation Dialog props
interface ChangePhoneDialogProps {
  open: boolean;
  title: any;
  subtitle: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  onClose(): void;
  selectedGuarantor: any;
  groupId: string | number;
  clinicId?: string | number;
  showSendRemainder?: boolean;
}

const getPhoneNumber = (phone: string): string => {
  let phoneNumber = phone;
  if (!phoneNumber) {
    return '';
  }
  if (phoneNumber.substr(0, 1) != '+') {
    phoneNumber = '+1' + phoneNumber;
  }
  return phoneNumber;
};

const ChangePhoneDialog = (
  props: ChangePhoneDialogProps
): React.ReactElement => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [loader, setLoader] = React.useState(false);
  //   const ClinicClasses = FilterClinicStyles();
  const [sendReminder, setReminderStatus] = React.useState(false);
  const submitButtonRef = React.useRef<HTMLButtonElement | null>(null);
  // props de-structure
  const {
    open,
    onClose,
    title,
    subtitle,
    selectedGuarantor,
    groupId,
    clinicId = '',
    showSendRemainder
  } = props;

  const handleClose = React.useCallback(() => {
    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose]);

  const {control, getValues, handleSubmit, errors, formState} = useForm<{
    notificationPhone: string;
  }>({
    mode: 'all',
    defaultValues: {
      notificationPhone: getPhoneNumber(selectedGuarantor.notification_phone)
    },
    resolver: async (values: any) => {
      return {values, errors: validateNotificationPhoneHookForm(values)};
    }
  });

  const checkFormState = (): void => {
    const {isValid} = formState;
    if (!isValid) {
      enqueueSnackbar(
        t('VALIDATORS.REQUIRED_FORM_FIELD_MESSAGE'),
        SnackBarConfig('w')
      );
    }
  };

  const handleUpdateNotificationPhoneButtonAction = async (
    data: any
  ): Promise<void> => {
    try {
      setLoader(true);
      const notificationPhone: string = getValues('notificationPhone');
      const updateResponse = await updateGuarantorNotificationPhone(
        groupId,
        data.id,
        {
          notification_phone: notificationPhone
        }
      );
      if (updateResponse) {
        enqueueSnackbar(
          t(
            updateResponse.message ??
              t('DUE_INVOICES.NOTIFICATION_DIALOG.NOTIFICATION_PHONE_UPDATED')
          ),
          SnackBarConfig('s')
        );
        if (sendReminder) {
          const reminderStatus = await sendReminders(groupId, clinicId, {
            guarantors: [data.id],
            reminder_type: ['sms'],
            due_type: '',
            select_all: false
          });
          if (reminderStatus) {
            enqueueSnackbar(
              t('DUE_INVOICES.MESSAGE_SENT'),
              SnackBarConfig('i')
            );
          }
          setReminderStatus(false);
        }
      }
      handleClose();
    } catch (error) {
      enqueueSnackbar(error && error.message, SnackBarConfig('e'));
      // throw error;
    } finally {
      setLoader(false);
    }
  };

  const body = () => {
    return (
      <div>
        <FormControl
          error={!!errors.notificationPhone}
          fullWidth={true}
          variant="outlined">
          <Controller
            id="notificationPhone"
            render={({value, onBlur, onChange}) => (
              <PhoneInputField
                value={value}
                onBlur={onBlur}
                inputProps={{
                  name: 'notificationPhone',
                  id: 'notificationPhone',
                  required: true
                }}
                disableCountryCode={false}
                disableCountryGuess={false}
                country={'us'}
                onlyCountries={undefined}
                onChange={(val, country, event) => {
                  event.persist();
                  onChange(event);
                }}
              />
            )}
            margin={'dense'}
            control={control}
            name={'notificationPhone'}
            type={'text'}
            placeholder={t('FORM.PLACEHOLDERS.NOTIFICATION_PHONE')}
            defaultValue=""
            error={!!errors.notificationPhone}
          />
          {errors.notificationPhone && (
            <FormHelperText>
              {t(errors.notificationPhone as unknown as string)}
            </FormHelperText>
          )}
        </FormControl>
      </div>
    );
  };

  return (
    <Dialog
      maxWidth={'md'}
      open={open}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      onClose={handleClose}
      aria-labelledby="confirm-dialog"
      aria-describedby="confirm-dialog">
      <DialogTitle disableTypography>{title}</DialogTitle>
      <form
        onSubmit={handleSubmit(() => {
          handleUpdateNotificationPhoneButtonAction(selectedGuarantor);
        })}>
        <DialogContent dividers>
          <div>{subtitle}</div>
          {body()}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={loader}
            onClick={() => handleClose()}
            variant="text"
            // size={'small'}
          >
            {t('DUE_INVOICES.NOTIFICATION_DIALOG.BUTTON_CANCEL')}
          </Button>
          {showSendRemainder && (
            <Button
              disabled={loader}
              variant="outlined"
              style={{marginRight: '16px'}}
              component={'label'}
              onClick={(): void => {
                setReminderStatus(true);
                setTimeout(() => {
                  if (
                    submitButtonRef?.current != null &&
                    typeof submitButtonRef?.current.click === 'function'
                  ) {
                    submitButtonRef.current.click();
                  }
                }, 100);
              }}
              // size={'small'}
              color="secondary">
              {t('DUE_INVOICES.NOTIFICATION_DIALOG.BUTTON_UPDATE_SEND', {
                amount: `${HelperUtils.formatCurrencyAmount(
                  selectedGuarantor.balance_amount,
                  true
                )}`
              })}
            </Button>
          )}
          <Button
            ref={submitButtonRef}
            disabled={loader}
            variant="contained"
            onClick={checkFormState}
            type="submit"
            // size={'small'}
            color="primary">
            {t('DUE_INVOICES.NOTIFICATION_DIALOG.BUTTON_UPDATE')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ChangePhoneDialog;
